<script setup lang="ts">
import Carousel from "primevue/carousel";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { BREAKPOINTS } from "@/breakpoints";
import { useMediaQuery } from "@/composables/useMobile";
import { useTenantTheming } from "@/composables/useTenantTheming.ts";
import { formatterWithoutCents } from "@/util/formatter";
import ExperienceCard from "./ExperienceCard.vue";
import { curatedCarouselPreset } from "./styles";
import type { ExperiencesCarouselSectionProps, ProgramExcursion } from "./types";

defineOptions({
  name: "ExperiencesCarouselSection",
});

const DISPLAY_COUNT: number = 3;
const MOBILE_DISPLAY_COUNT: number = 1;
// Per-item carousel slot width (matches the configurator's ExcursionIncludedSection).
const CARD_WIDTH_PX: number = 393;
// Horizontal padding ExperienceCard carries so neighbouring cards keep a gutter (`px-2` = 8px).
const CARD_GUTTER_PX: number = 8;

const props = withDefaults(defineProps<ExperiencesCarouselSectionProps>(), {
  banded: false,
  showPrices: false,
});

// `secondaryColor` is the app-wide "branded storefront" signal (Menu.vue, ExperienceBaseFooter).
// Branded tenants get a gray accent band with navy copy — the storefront color is reserved for
// controls (the carousel buttons, the card's Learn More CTA), not for surfaces and text.
const { secondaryColor, primaryColorClassBG } = useTenantTheming();

// Carousel prev/next buttons follow the storefront primary branding.
const carouselPreset = computed(() => curatedCarouselPreset(primaryColorClassBG.value));

// Viewport width, not device detection: the card layout switches on `md:` utilities, so the
// carousel has to agree with those. `- 1` mirrors Tailwind's boundary — `md:` applies from
// exactly 900px up, so "below md" must stop at 899px.
const isNarrowViewport = useMediaQuery(`(max-width: ${BREAKPOINTS.md - 1}px)`);

// Show one card at a time on mobile so the carousel never overflows the viewport. PrimeVue
// compiles this to `@media (max-width: <breakpoint>)`, which is inclusive, so it flips exactly
// where the cards' `md:` styles flip.
const responsiveOptions = [{ breakpoint: `${BREAKPOINTS.md - 1}px`, numVisible: MOBILE_DISPLAY_COUNT, numScroll: 1 }];

const displayCount = computed(() =>
  props.excursions.length < DISPLAY_COUNT ? props.excursions.length : DISPLAY_COUNT,
);

const visibleCount = computed(() =>
  isNarrowViewport.value ? Math.min(props.excursions.length, MOBILE_DISPLAY_COUNT) : displayCount.value,
);

const showNavigation = computed(() => props.excursions.length > visibleCount.value);

// On mobile a card fills the width, so prev/next would land on top of the card's own content
// (they are absolutely positioned at the carousel's mid-height). Paging there is the dots plus
// the Carousel's built-in touch swipe; the arrows are desktop-only.
const showNavigatorArrows = computed(() => showNavigation.value && !isNarrowViewport.value);

// With fewer than a full row of cards, cap the carousel width so each card keeps its intended
// size instead of stretching to fill the section. Desktop-only: on mobile the carousel fills
// the available width.
const useCompactCarousel = computed(
  () => !isNarrowViewport.value && props.excursions.length > 0 && props.excursions.length < DISPLAY_COUNT,
);

// The card's own `px-2` also insets the first and last card from the section edges, which reads
// as a stray gap beside the flush-left title. The banded variant absorbs it into its `p-4`; the
// unbanded one cancels it with a matching negative margin so the outer cards sit flush and only
// the inter-card gutters remain.
const cancelCardGutter = computed(() => !props.banded);

const compactCarouselWidth = computed(() => {
  const base = displayCount.value * CARD_WIDTH_PX;
  return `${cancelCardGutter.value ? base + CARD_GUTTER_PX * 2 : base}px`;
});

// A single width utility: `w-full` and `w-[calc(...)]` are both `width`, so emitting both would
// leave the winner up to stylesheet order.
const carouselWidthClass = computed(() => {
  if (useCompactCarousel.value) {
    return cancelCardGutter.value ? "max-w-[calc(100%+1rem)]" : "max-w-full";
  }
  return cancelCardGutter.value ? "w-[calc(100%+1rem)]" : "w-full";
});

// The arrows sit on the seam between a card's image and its text. That seam is not a fixed
// offset: the image is `aspect-ratio: 377/212` on a fluid card width, floored by a 212px
// min-height, so it moves with the viewport and clamps on narrow cards. PrimeVue has no option
// for this — its nav buttons are absolutely positioned against the carousel container and know
// nothing about the item template — so the seam is measured and handed to the preset as a
// custom property. Falls back to the carousel's mid-height until the first measurement lands.
const carouselRoot = ref<HTMLElement>();
const navTopOffset = ref<string>("50%");

const measureNavOffset = (): void => {
  const root = carouselRoot.value;
  const media = root?.querySelector<HTMLElement>("[data-experience-card-media]");
  if (!root || !media) return;
  navTopOffset.value = `${Math.round(media.getBoundingClientRect().bottom - root.getBoundingClientRect().top)}px`;
};

let navResizeObserver: ResizeObserver | undefined;

onMounted(async () => {
  await nextTick();
  measureNavOffset();

  if (typeof ResizeObserver === "undefined" || !carouselRoot.value) return;
  navResizeObserver = new ResizeObserver(measureNavOffset);
  navResizeObserver.observe(carouselRoot.value);
});

onBeforeUnmount(() => {
  navResizeObserver?.disconnect();
  navResizeObserver = undefined;
});

// Cards arriving (or the count crossing the single-card breakpoint) changes the seam.
watch(
  () => props.excursions.length,
  async () => {
    await nextTick();
    measureNavOffset();
  },
);

const carouselStyle = computed<Record<string, string>>(() => ({
  ...(useCompactCarousel.value ? { width: compactCarouselWidth.value } : {}),
  "--carousel-nav-top": navTopOffset.value,
}));

const priceLabel = (excursion: ProgramExcursion): string => {
  if (!props.showPrices || !excursion.occurrence_price_student) return "";
  return `+${formatterWithoutCents.format(excursion.occurrence_price_student / 100)}`;
};
</script>

<template>
  <div
    :class="banded ? 'shadow-[0px_4px_20px_0px_#00000026]' : ''"
    data-testid="experiences-carousel-section"
  >
    <div
      class="flex w-full justify-start items-center"
      :class="banded ? ['py-2 px-4', secondaryColor ? 'bg-gray-275' : 'bg-blue-400'] : ''"
    >
      <span class="text-xl font-bold text-university-primary" data-testid="experiences-carousel-section-title">
        {{ title }}
      </span>
    </div>
    <div class="flex flex-col gap-4" :class="banded ? 'p-4' : 'pt-2'">
      <div class="w-full text-university-primary">
        <span data-testid="experiences-carousel-section-description">{{ description }}</span>
      </div>
      <div
        ref="carouselRoot"
        :class="[carouselWidthClass, cancelCardGutter ? '-mx-2' : '']"
        :style="carouselStyle"
        data-testid="experiences-carousel"
      >
        <Carousel
          :value="excursions"
          :numVisible="displayCount"
          :responsiveOptions="responsiveOptions"
          :showIndicators="showNavigation"
          :showNavigators="showNavigatorArrows"
          :numScroll="1"
          :circular="showNavigation"
          :pt="carouselPreset"
        >
          <template #item="{ data: excursion }">
            <ExperienceCard :excursion="excursion" :price-label="priceLabel(excursion)" />
          </template>
        </Carousel>
      </div>
    </div>
  </div>
</template>
